import {Button, IconButton} from '@wandb/components';
import {RunState} from '@wandb/weave/components/RunState';
import React, {useEffect, useRef, useState} from 'react';

import {useNavigateTo} from '../../util/history';
import {useIsTextOverflowing} from '../../util/hooks/useIsTextOverflowing';
import {safeLocalStorage} from '../../util/localStorage';
import {InstrumentedWaveLoader as Loader} from '../utility/InstrumentedLoader';
import {Queue} from './Queues/queueUtil';
import * as S from './RunQueueItemsPopoverContent.styles';
import {
  formatRelativeTime,
  type RunQueuePopoverItem,
} from './runQueuePopoverUtil';
import {PUBLIC_JOBS_INFO} from './utils';

interface RunQueueItemsPopoverContentProps {
  queue: Queue;
  items: RunQueuePopoverItem[];
  loading: boolean;
}

const ITEMS_PER_PAGE = 5;

const shouldShowLeaderboard = (item: RunQueuePopoverItem): boolean => {
  if (!item.jobName || !item.state) {
    return false;
  }

  const isCompleted = item.state === 'finished';
  if (!isCompleted) {
    return false;
  }

  const leaderboardConfigOverrideKey =
    PUBLIC_JOBS_INFO[item.jobName]?.leaderboardConfigOverrideKey;

  if (!leaderboardConfigOverrideKey) {
    return false;
  }

  return (
    item.jobName in PUBLIC_JOBS_INFO &&
    item.runSpecConfigOverrides?.[leaderboardConfigOverrideKey] === true
  );
};

const getLeaderboardPath = (
  entityName: string,
  projectName: string,
  jobName: string
): string => {
  const leaderboardName = PUBLIC_JOBS_INFO[jobName]?.leaderboardName;
  return `/${entityName}/${projectName}/weave/leaderboards/${leaderboardName}`;
};

const RelativeTimestamp: React.FC<{createdAt: string}> = ({createdAt}) => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  return <span>{formatRelativeTime(createdAt)}</span>;
};

const MiddleTruncatedText: React.FC<{text: string}> = ({text}) => {
  const parentDomRef = useRef<HTMLDivElement | null>(null);
  const isTextOverflowing = useIsTextOverflowing({
    text,
    parentDomRef,
  });

  if (!isTextOverflowing) {
    return <span>{text}</span>;
  }

  // middle truncation this is based on a column max-width of
  // 200px and the Source Sans Pro font @ 15px.
  const maxLength = 25;
  if (text.length <= maxLength) {
    return <span>{text}</span>;
  }

  const ellipsis = '...';
  const availableChars = maxLength - ellipsis.length;
  const frontChars = Math.ceil(availableChars / 2);
  const backChars = Math.floor(availableChars / 2);

  const frontText = text.slice(0, frontChars);
  const backText = text.slice(-backChars);

  return (
    <div ref={parentDomRef} className="flex w-full min-w-0">
      <span className="shrink truncate">{frontText}</span>
      <span className="shrink-0">{ellipsis}</span>
      <span className="shrink truncate">{backText}</span>
    </div>
  );
};

export const RunQueueItemsPopoverContent: React.FC<
  RunQueueItemsPopoverContentProps
> = ({queue, items: userRunQueueItems, loading}) => {
  const navigateTo = useNavigateTo();
  const [currentPage, setCurrentPage] = useState(1);

  const totalItems = userRunQueueItems.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);

  const currentItems = userRunQueueItems.slice(startIndex, endIndex);

  const getRunUrl = (item: RunQueuePopoverItem) => {
    const runName = item.runName || item.associatedRunId;
    if (runName && item.state) {
      const baseUrl = `/${item.entityName}/${item.projectName}/runs/${runName}`;
      // link to logs for failed runs
      return item.state === 'failed' ? `${baseUrl}/logs` : baseUrl;
    }
    return null;
  };

  const [openAfterEachLaunch, setOpenAfterEachLaunch] = useState(() => {
    try {
      const stored = safeLocalStorage.getItem('openPopoverAfterEachLaunch');
      return stored ? stored === 'true' : true;
    } catch (e) {
      return true;
    }
  });

  const handleOpenToggle = (checked: boolean) => {
    setOpenAfterEachLaunch(checked);
    try {
      safeLocalStorage.setItem(
        'openPopoverAfterEachLaunch',
        checked.toString()
      );
    } catch (e) {
      console.warn(
        'Failed to save launch popover preference to localStorage:',
        e
      );
    }
  };

  return (
    <S.PopoverContainer>
      <S.Header>
        <S.HeaderTitle>Recent runs</S.HeaderTitle>
      </S.Header>

      {loading ? (
        <S.LoadingContainer>
          <Loader name="tab-runs-loader" size="small" />
        </S.LoadingContainer>
      ) : totalItems === 0 ? (
        <S.EmptyStateContainer>No recent runs</S.EmptyStateContainer>
      ) : (
        <>
          <S.TableContainer>
            <S.TableContent>
              <S.TableHeaderRow>
                <S.TableHeaderCell padding="8px 8px 8px 16px">
                  Submitted
                </S.TableHeaderCell>
                <S.TableHeaderCell>Run</S.TableHeaderCell>
                <S.TableHeaderCell>Queue</S.TableHeaderCell>
                <S.TableHeaderCell>Project</S.TableHeaderCell>
                <S.TableHeaderCell></S.TableHeaderCell>
                <S.TableHeaderCell padding="8px 16px 8px 8px">
                  State
                </S.TableHeaderCell>
              </S.TableHeaderRow>
              {Array.from({length: ITEMS_PER_PAGE}, (_, index) => {
                const item = currentItems[index];

                if (!item) {
                  // fill with empty row for consistent height
                  return (
                    <S.TableRow key={`empty-${index}`}>
                      <S.TimeColumn />
                      <S.RunColumn />
                      <S.QueueColumn />
                      <S.ProjectColumn />
                      <S.LeaderboardColumn />
                      <S.StatusColumn />
                    </S.TableRow>
                  );
                }

                const runUrl = getRunUrl(item);
                return (
                  <S.TableRow key={item.id}>
                    <S.TimeColumn>
                      <RelativeTimestamp createdAt={item.createdAt} />
                    </S.TimeColumn>

                    <S.RunColumn>
                      {runUrl && item.type === 'RUN' ? (
                        <S.RunLink to={runUrl}>
                          <MiddleTruncatedText
                            text={
                              item.runDisplayName ||
                              item.runName ||
                              item.associatedRunId ||
                              'Pending run'
                            }
                          />
                        </S.RunLink>
                      ) : (
                        <span>
                          <MiddleTruncatedText
                            text={
                              item.runDisplayName ||
                              item.runName ||
                              item.associatedRunId ||
                              'Pending run'
                            }
                          />
                        </span>
                      )}
                    </S.RunColumn>
                    <S.QueueColumn>
                      {item.queueId && item.queueName ? (
                        <S.ClickableText
                          onClick={() =>
                            navigateTo({
                              location: {
                                pathname: `/${queue.entityName}/launch/${item.queueId}`,
                              },
                            })
                          }>
                          <MiddleTruncatedText text={item.queueName} />
                        </S.ClickableText>
                      ) : (
                        <span style={{color: '#79808A', fontWeight: 400}}>
                          No queue
                        </span>
                      )}
                    </S.QueueColumn>
                    <S.ProjectColumn>
                      <S.ClickableText
                        onClick={() =>
                          navigateTo({
                            location: {
                              pathname: `/${item.entityName}/${item.projectName}`,
                            },
                          })
                        }>
                        <MiddleTruncatedText text={item.projectName} />
                      </S.ClickableText>
                    </S.ProjectColumn>
                    <S.LeaderboardColumn>
                      {shouldShowLeaderboard(item) && (
                        <Button
                          startIcon="benchmark-square"
                          variant="ghost"
                          size="sm"
                          aria-label="View leaderboard"
                          onClick={() => {
                            const leaderboardPath = getLeaderboardPath(
                              item.entityName,
                              item.projectName,
                              item.jobName!
                            );
                            navigateTo({
                              location: {
                                pathname: leaderboardPath,
                              },
                            });
                          }}>
                          Leaderboard
                        </Button>
                      )}
                    </S.LeaderboardColumn>
                    <S.StatusColumn>
                      <RunState value={item.state} />
                    </S.StatusColumn>
                  </S.TableRow>
                );
              })}
            </S.TableContent>

            <S.PaginationContainer>
              <S.PaginationText>
                {totalItems > 0
                  ? `${startIndex + 1}-${endIndex} of ${totalItems}`
                  : '0 of 0'}
              </S.PaginationText>

              <div style={{display: 'flex', gap: '4px'}}>
                <IconButton
                  icon="chevron-back"
                  variant="ghost"
                  aria-label="Previous page"
                  isDisabled={currentPage === 1}
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                />

                <IconButton
                  icon="chevron-next"
                  variant="ghost"
                  aria-label="Next page"
                  isDisabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                />
              </div>
            </S.PaginationContainer>
          </S.TableContainer>
          <S.CheckboxContainer>
            <input
              type="checkbox"
              id="auto-open"
              checked={openAfterEachLaunch}
              onChange={e => handleOpenToggle(e.target.checked)}
            />
            <label htmlFor="auto-open">Open automatically after launch</label>
          </S.CheckboxContainer>
        </>
      )}
    </S.PopoverContainer>
  );
};
